// ABOUTME: The /applications/:applicationRefId route: one application with its timeline and the withdrawal, for
// ABOUTME: talents in the PRO_ATS tracker arm, as the page of the job applied to when pro-core serves that job.
import { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { PageHeader } from 'components/PageHeader';
import { NotFoundErrorScreen } from 'components/screens';
import { ApplicationDetail, useTrackerArm } from 'modules/applications';
import { getTrans } from 'modules/i18n';
import {
  ChangeWeek,
  JobListingHeader,
  JobListingSections,
  JobListingSectionsSkeleton,
  useApplyV2Enabled,
  useJobPage
} from 'modules/jobListings';
import { Layout } from 'modules/minimal-ui-theme';

/**
 * Behind the same arm as the list: for a control talent this route does not exist, exactly as the backend answers
 * 404 for them. A route matched without its parameter cannot name an application, so it is not-found too.
 *
 * PJL: pro-core serves the job of every application (GET /talent/applications/{refId}/job, from its listing, its opening
 * or its assignment) while PRO_JOB_LISTING and PRO_APPLY_V2 are on; then the page is the job's. Off, a 404 or a failed
 * read leave it the application page it was.
 */
export const ApplicationDetailPage = () => {
  const { variant, isTracker, isLoading } = useTrackerArm();
  const { applicationRefId } = useParams();
  const { isEnabled: applyV2 } = useApplyV2Enabled();
  const job = useJobPage(
    'application',
    isTracker ? applicationRefId : undefined
  );

  if (isLoading) return null;

  if (!isTracker || !applicationRefId) {
    return <NotFoundErrorScreen />;
  }

  const isJobPage = job.state === 'ready';
  let jobDetails: ReactNode;
  if (job.state === 'ready')
    jobDetails = <JobListingSections listing={job.job} />;
  if (job.state === 'loading') jobDetails = <JobListingSectionsSkeleton />;

  return (
    <Layout
      header={
        <PageHeader
          title={getTrans(
            isJobPage ? 'jobs.detail.title' : 'applicationDetailPage.title'
          )}
          withBackBtn
        />
      }
    >
      <ApplicationDetail
        refId={applicationRefId}
        variant={variant}
        renderJobHeader={
          job.state === 'ready'
            ? badges => <JobListingHeader listing={job.job} badges={badges} />
            : undefined
        }
        jobDetails={jobDetails}
        renderChangeWeek={application =>
          // PJL: the week editor of the apply flow, against the listing applied to; only with PRO_APPLY_V2.
          applyV2 && application.jobListingRefId ? (
            <ChangeWeek
              applicationRefId={application.refId}
              listingRefId={application.jobListingRefId}
            />
          ) : null
        }
      />
    </Layout>
  );
};
